// Small display formatters shared across the Solution Studio panels (split out of the old
// monolithic ProjectWorkspace.js).

// Compact relative-time ("2h ago"); falls back to a date for older stamps.
export function timeAgo(ts) {
  if (!ts) return 'unknown';
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

// The lock/createdBy identity may be an Azure AD oid; show a short prefix rather than the full guid.
export function shortBy(who) {
  return /^[0-9a-f-]{20,}$/i.test(who) ? `${who.slice(0, 8)}…` : who;
}

// Format a USD amount for the AI-cost readouts: exact $0 stays $0.00, tiny non-zero shows < $0.01,
// everything else 2dp. Mirrors the cents-to-low-dollars range a Design pass lands in.
export function formatUsd(n) {
  const v = Number(n) || 0;
  if (v === 0) return '$0.00';
  if (v < 0.01) return '< $0.01';
  return `$${v.toFixed(2)}`;
}

// Collections that are PROCESS/meta artifacts, not design CONTENT: a model carrying ONLY these (e.g.
// the auto-generated open questions a failed/empty pass leaves behind) is not "a design" yet.
const NON_CONTENT_COLLECTIONS = new Set(['openQuestions', 'decisions']);

// True when the Design Model has ≥1 non-empty CONTENT collection (intents / flows / queues / prompts /
// operational resources / …). Open questions + decisions alone do NOT count as "has model", so an
// otherwise-empty model doesn't unlock Refine/Diagrams/Documents as though a design exists.
export function hasDesignContent(designModel) {
  return !!designModel && Object.keys(designModel).some(
    (k) => !NON_CONTENT_COLLECTIONS.has(k) && Array.isArray(designModel[k]) && designModel[k].length
  );
}

// One-line by-model breakdown for the cost tooltip (e.g. "opus: $0.31 · sonnet: $0.08").
export function costByModelLabel(byModel) {
  const parts = Object.entries(byModel || {})
    .filter(([, b]) => (b?.costUsd || 0) > 0)
    .map(([tier, b]) => `${tier}: ${formatUsd(b.costUsd)}`);
  return parts.length ? parts.join(' · ') : null;
}
